import {SelectionModel} from '@angular/cdk/collections';
import {CurrencyPipe} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute} from '@angular/router';
import {TargetService, TtdCategory} from '../../target/target.service';
import {SubscriptionHandler} from '../../utils/subscription-handler';
import {TtdCategoryCreateComponent} from './create/ttd-category-create.component';

export enum CategoryState {
  TTD = 'TTD',
  TTD_DATA_ALLIANCE = 'TTD_DATA_ALLIANCE'
}


@Component({
  selector: 'app-ttd-category-config',
  template: `
    <div style="text-align: center;">
      <mat-button-toggle-group style="margin: 10px" name="fontStyle" aria-label="Font Style"
                               [value]="activeCategoryState"
                               (change)="changeState()">
        <mat-button-toggle *ngFor="let state of categoryStateKeys" [value]="state"
                           [matTooltip]="'Displays '+state.toLowerCase()+' TheTradeDesk taxonomies'">
          {{ getTitleForCategoryState(state) }}
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <div class="actions">
      <button (click)="openAddDialog()"
              [disabled]="!filteredCategories"
              id="btn-add-ttd-category" mat-mini-fab
              data-cy="button-ttd-category-add"
              matTooltip="Add"
              name="create-ttd-category">
        <mat-icon>add</mat-icon>
      </button>

      <mat-spinner *ngIf="notReady" style="position: absolute; right: 20px" color="accent" diameter="32"></mat-spinner>

      <mat-form-field data-cy="input-category-filter">
        <mat-label>Filter by Name or Category-Id</mat-label>
        <input (ngModelChange)="filter($event)"
               [(ngModel)]="filterValue"
               [autofocus]="true"
               autocomplete="off"
               matInput
               type="text"
               name="filter">
        <button (click)="filterValue=''; filter('')" *ngIf="filterValue" aria-label="Clear" mat-button mat-icon-button
                matSuffix>
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>
      <app-ttd-category-content [categories]="filteredCategories"></app-ttd-category-content>
    </div>
  `,
  styleUrls: ['./ttd-category-config.component.scss']
})

export class TtdCategoryConfigComponent implements OnInit, OnDestroy {
  categoryStateKeys = Object.keys(CategoryState);
  filteredCategories: TtdCategory[] = [];
  selection: SelectionModel<number> = new SelectionModel<number>(true, []);
  filterValue!: string;
  lastFilterValue!: string;
  notReady = true;
  activeCategoryState: CategoryState = CategoryState.TTD;
  private subscriptionHandler = new SubscriptionHandler();
  unfilteredCategories: TtdCategory[] = [];

  constructor(private targetService: TargetService,
              private dialog: MatDialog,
              public pipe: CurrencyPipe,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    this.filterFromRoute();
    this.fetchCategories();
  }

  ngOnDestroy() {
    this.subscriptionHandler.unsubscribe();
  }

  changeState() {
    this.activeCategoryState = this.activeCategoryState === CategoryState.TTD ? CategoryState.TTD_DATA_ALLIANCE : CategoryState.TTD;
    this.filter(this.lastFilterValue, this.activeCategoryState);
  }

  filterFromRoute() {
    this.lastFilterValue = this.route.snapshot?.queryParamMap.get('id') || '';
  }

  openAddDialog(): void {
    const ref = this.dialog.open(TtdCategoryCreateComponent, {
      data: {categories: this.filteredCategories, activeCategoryState: this.activeCategoryState},
    });

    this.subscriptionHandler.subscription = ref.afterClosed().subscribe(() => {
      this.fetchCategories({cache: false});
    });
  }

  fetchCategories(options: any = {}) {
    const cacheKey = 'cache';
    const noCache = options[cacheKey] === false;
    if (noCache) {
      this.notReady = true;
    }
    this.selection.clear();
    const startFetch = Date.now();

    const method = noCache && !window.hasOwnProperty('__karma__') ? 'getTtdCategoriesNoCache' : 'getTtdCategories';
    this.subscriptionHandler.subscription = this.targetService[method]()
      .subscribe((categories: TtdCategory[]) => {
        this.unfilteredCategories = categories;
        this.filteredCategories = categories;
        if (Date.now() - startFetch > 100) {
          this.notReady = false;
        }
        this.filter(this.lastFilterValue);
      });
  }

  filter(search: string, categoryState: CategoryState = this.activeCategoryState): void {
    this.lastFilterValue = search;
    if (!search) {
      this.filteredCategories = this.unfilteredCategories.filter((c) => {
        return (c.origin === CategoryState[categoryState]);
      });
      return;
    }
    this.filteredCategories = this.unfilteredCategories.filter((c) => {
      return (c.origin === CategoryState[categoryState]) &&
        (c.displayName.toLowerCase().includes(search.toLowerCase()) || c.providerElementId.includes(search));
    });
  }

  getTitleForCategoryState(state: string) {
    if (state === CategoryState.TTD) {
      return 'The Trade Desk';
    } else if (state === CategoryState.TTD_DATA_ALLIANCE) {
      return 'The Trade Desk: Data Alliance';
    }
    return '';
  }
}
